import { useEffect, useState, useMemo } from 'react';
import api, { API_BASE } from '../api';
import Layout from '../components/Layout';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { T } from '../styles/theme';
import { useIsMobile } from '../hooks/useIsMobile';

function formatDate(d) { return new Date(d).toLocaleDateString('it-IT'); }
function today() { const d = new Date(); return `${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}`; }

export default function Vendite() {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPiattaforma, setFilterPiattaforma] = useState('');
  const [filterMese, setFilterMese] = useState('');
  const isMobile = useIsMobile();

  useEffect(() => {
    api.get('/sales').then((r) => setSales(Array.isArray(r.data) ? r.data : [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const mesiDisponibili = useMemo(() => {
    const set = new Set();
    (sales || []).forEach((s) => {
      const d = new Date(s.data_vendita);
      set.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    });
    return Array.from(set).sort().reverse();
  }, [sales]);

  const filtered = useMemo(() => {
    return (sales || []).filter((s) => {
      if (filterPiattaforma && s.piattaforma !== filterPiattaforma) return false;
      if (filterMese) {
        const d = new Date(s.data_vendita);
        if (`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` !== filterMese) return false;
      }
      return true;
    });
  }, [sales, filterPiattaforma, filterMese]);

  const totals = useMemo(() => {
    return (filtered || []).reduce((a, s) => ({
      vendite: a.vendite + s.prezzo_vendita,
      commissioni: a.commissioni + s.commissioni,
      profitto: a.profitto + s.profitto_netto,
    }), { vendite: 0, commissioni: 0, profitto: 0 });
  }, [filtered]);

  const formatMese = (m) => {
    const [y, mo] = m.split('-');
    const mesi = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
    return `${mesi[parseInt(mo)-1]} ${y}`;
  };

  const exportPDF = () => {
    try {
      const doc = new jsPDF();

      // Header
      doc.setFillColor(13, 11, 30);
      doc.rect(0, 0, 210, 35, 'F');
      doc.setFillColor(124, 58, 237);
      doc.rect(0, 35, 210, 1, 'F');
      doc.setTextColor(167, 139, 250);
      doc.setFontSize(22);
      doc.text('IMPERO ELITE', 14, 18);
      doc.setFontSize(10);
      doc.setTextColor(226, 232, 240);
      doc.text('Report Vendite', 14, 26);
      doc.setFontSize(8);
      doc.setTextColor(107, 114, 128);
      doc.text(`Generato il ${new Date().toLocaleDateString('it-IT')} alle ${new Date().toLocaleTimeString('it-IT', {hour:'2-digit',minute:'2-digit'})}`, 14, 32);

      // KPI boxes
      const kpiY = 42;
      const kpiW = 58;
      const kpiH = 20;

      // Vendite
      doc.setFillColor(28, 22, 64);
      doc.roundedRect(14, kpiY, kpiW, kpiH, 3, 3, 'F');
      doc.setFontSize(7);
      doc.setTextColor(107, 114, 128);
      doc.text('VENDITE TOTALI', 16, kpiY + 6);
      doc.setFontSize(14);
      doc.setTextColor(226, 232, 240);
      doc.text(`${filtered.length} vendite`, 16, kpiY + 15);

      // Commissioni
      doc.setFillColor(28, 22, 64);
      doc.roundedRect(76, kpiY, kpiW, kpiH, 3, 3, 'F');
      doc.setFontSize(7);
      doc.setTextColor(107, 114, 128);
      doc.text('COMMISSIONI', 78, kpiY + 6);
      doc.setFontSize(14);
      doc.setTextColor(248, 113, 113);
      doc.text(`-\u20AC${totals.commissioni.toFixed(2)}`, 78, kpiY + 15);

      // Profitto
      doc.setFillColor(28, 22, 64);
      doc.roundedRect(138, kpiY, kpiW, kpiH, 3, 3, 'F');
      doc.setFontSize(7);
      doc.setTextColor(107, 114, 128);
      doc.text('PROFITTO NETTO', 140, kpiY + 6);
      doc.setFontSize(14);
      doc.setTextColor(74, 222, 128);
      doc.text(`\u20AC${totals.profitto.toFixed(2)}`, 140, kpiY + 15);

      // Table
      const rows = (filtered || []).map((s) => [
        (s.product_name || '').substring(0, 35),
        `\u20AC${s.prezzo_acquisto.toFixed(2)}`,
        `\u20AC${s.prezzo_vendita.toFixed(2)}`,
        `-\u20AC${s.commissioni.toFixed(2)}`,
        `\u20AC${s.profitto_netto.toFixed(2)}`,
        s.piattaforma || '-',
        formatDate(s.data_vendita),
      ]);

      autoTable(doc, {
        startY: kpiY + kpiH + 8,
        head: [['Prodotto', 'Acquisto', 'Vendita', 'Commissioni', 'Profitto', 'Piattaforma', 'Data']],
        body: rows,
        foot: [[
          `TOTALI (${filtered.length})`, '',
          `\u20AC${totals.vendite.toFixed(2)}`,
          `-\u20AC${totals.commissioni.toFixed(2)}`,
          `\u20AC${totals.profitto.toFixed(2)}`,
          '', '',
        ]],
        headStyles: { fillColor: [28, 22, 64], textColor: [167, 139, 250], fontSize: 8, fontStyle: 'bold', halign: 'left' },
        footStyles: { fillColor: [13, 11, 30], textColor: [74, 222, 128], fontSize: 9, fontStyle: 'bold' },
        styles: { fontSize: 7.5, cellPadding: 4, textColor: [50, 50, 50] },
        alternateRowStyles: { fillColor: [248, 248, 255] },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { halign: 'right' },
          2: { halign: 'right' },
          3: { halign: 'right', textColor: [200, 50, 50] },
          4: { halign: 'right', fontStyle: 'bold' },
          5: { halign: 'center' },
          6: { halign: 'center' },
        },
      });

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(150, 150, 150);
        doc.text(`Impero Elite Dashboard \u2022 dashboard.proelite.it \u2022 Pagina ${i}/${pageCount}`, 105, 290, { align: 'center' });
      }

      doc.save(`vendite_impero_elite_${today()}.pdf`);
    } catch (err) {
      console.error('Errore PDF:', err);
      alert('Errore nella generazione del PDF. Riprova.');
    }
  };

  const exportExcel = () => {
    const venditeData = filtered.map((s) => ({
      'Prodotto': s.product_name || '',
      'Piattaforma': s.piattaforma || '',
      'Data Vendita': formatDate(s.data_vendita),
      'Prezzo Acquisto (\u20AC)': s.prezzo_acquisto,
      'Prezzo Vendita (\u20AC)': s.prezzo_vendita,
      'Commissioni (\u20AC)': s.commissioni,
      'Profitto Netto (\u20AC)': s.profitto_netto,
      'ROI (%)': s.prezzo_acquisto > 0 ? Math.round((s.profitto_netto / s.prezzo_acquisto) * 100) : 0,
    }));

    // Riga totali
    venditeData.push({
      'Prodotto': `TOTALI (${filtered.length} vendite)`,
      'Piattaforma': '',
      'Data Vendita': '',
      'Prezzo Acquisto (\u20AC)': '',
      'Prezzo Vendita (\u20AC)': totals.vendite,
      'Commissioni (\u20AC)': totals.commissioni,
      'Profitto Netto (\u20AC)': totals.profitto,
      'ROI (%)': '',
    });

    // Riepilogo per piattaforma
    const piattaforme = {};
    filtered.forEach((s) => {
      const p = s.piattaforma || 'Altro';
      if (!piattaforme[p]) piattaforme[p] = { 'Piattaforma': p, 'N. Vendite': 0, 'Totale Venduto (\u20AC)': 0, 'Commissioni (\u20AC)': 0, 'Profitto (\u20AC)': 0 };
      piattaforme[p]['N. Vendite']++;
      piattaforme[p]['Totale Venduto (\u20AC)'] += s.prezzo_vendita;
      piattaforme[p]['Commissioni (\u20AC)'] += s.commissioni;
      piattaforme[p]['Profitto (\u20AC)'] += s.profitto_netto;
    });

    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.json_to_sheet(venditeData);
    ws1['!cols'] = [{ wch: 35 }, { wch: 12 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, ws1, 'Vendite');

    const ws2 = XLSX.utils.json_to_sheet(Object.values(piattaforme));
    ws2['!cols'] = [{ wch: 15 }, { wch: 12 }, { wch: 18 }, { wch: 16 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, ws2, 'Riepilogo Piattaforme');

    XLSX.writeFile(wb, `vendite_impero_elite_${today()}.xlsx`);
  };

  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (saleId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa vendita? Il prodotto tornera disponibile.')) return;
    setDeleting(saleId);
    try {
      await api.delete(`/sales/${saleId}`);
      setSales((prev) => prev.filter((s) => s.id !== saleId));
    } catch {
      alert('Errore durante l\'eliminazione della vendita');
    } finally {
      setDeleting(null);
    }
  };

  const platBadge = (p) => {
    if (p === 'eBay') return { bg: 'rgba(34,211,238,0.12)', color: T.cyan, border: 'rgba(34,211,238,0.25)' };
    if (p === 'Vinted') return { bg: 'rgba(74,222,128,0.12)', color: T.green, border: 'rgba(74,222,128,0.25)' };
    return { bg: 'rgba(167,139,250,0.12)', color: T.violet, border: 'rgba(167,139,250,0.25)' };
  };

  return (
    <Layout>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="page-title">Vendite</h1>
            <p className="mt-1" style={{ color: T.muted, fontFamily: T.fontBody, fontSize: '0.85rem' }}>Storico completo delle vendite</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select value={filterPiattaforma} onChange={(e) => setFilterPiattaforma(e.target.value)} className="input text-sm" style={{ width: 'auto', minWidth: 130 }}>
              <option value="">Tutte</option>
              <option value="eBay">eBay</option>
              <option value="Vinted">Vinted</option>
              <option value="Altro">Altro</option>
            </select>
            <select value={filterMese} onChange={(e) => setFilterMese(e.target.value)} className="input text-sm" style={{ width: 'auto', minWidth: 120 }}>
              <option value="">Tutti i mesi</option>
              {mesiDisponibili.map((m) => <option key={m} value={m}>{formatMese(m)}</option>)}
            </select>
            <button onClick={exportPDF} className="btn-secondary text-sm" style={{ padding: '6px 14px' }}>PDF</button>
            <button onClick={exportExcel} className="text-sm font-semibold" style={{ padding: '6px 14px', borderRadius: 9999, background: T.green, color: T.bg, border: 'none', cursor: 'pointer' }}>Excel</button>
          </div>
        </div>

        {/* KPI */}
        {!loading && filtered.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: isMobile ? 8 : 16, marginBottom: 24 }}>
            <div className="card" style={{ padding: isMobile ? 14 : 20, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <p style={{ color: T.muted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: T.fontBody, margin: '0 0 4px' }}>Vendite</p>
              <p style={{ color: T.text, fontSize: isMobile ? '1.2rem' : '1.6rem', fontWeight: 800, fontFamily: T.fontDisplay, margin: '0 0 2px' }}>{filtered.length}</p>
              <p style={{ color: T.text, fontSize: '0.8rem', fontFamily: T.fontBody, margin: 0 }}>{'\u20AC'}{totals.vendite.toFixed(2)}</p>
            </div>
            <div className="card" style={{ padding: isMobile ? 14 : 20, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <p style={{ color: T.muted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: T.fontBody, margin: '0 0 4px' }}>Commissioni</p>
              <p style={{ color: T.red, fontSize: isMobile ? '1.2rem' : '1.6rem', fontWeight: 800, fontFamily: T.fontDisplay, margin: 0 }}>{'\u20AC'}{totals.commissioni.toFixed(2)}</p>
            </div>
            <div className="card" style={{ padding: isMobile ? 14 : 20, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <p style={{ color: T.muted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: T.fontBody, margin: '0 0 4px' }}>Profitto</p>
              <p style={{ color: totals.profitto >= 0 ? T.green : T.red, fontSize: isMobile ? '1.2rem' : '1.6rem', fontWeight: 800, fontFamily: T.fontDisplay, margin: 0 }}>{'\u20AC'}{totals.profitto.toFixed(2)}</p>
            </div>
          </div>
        )}

        {/* Lista */}
        {loading ? (
          <div className="text-center py-16" style={{ color: T.muted }}>Caricamento...</div>
        ) : filtered.length === 0 ? (
          <div className="card p-12 text-center">
            <p style={{ fontSize: '2rem', marginBottom: 8 }}>📦</p>
            <p style={{ color: T.muted, fontFamily: T.fontBody }}>Nessuna vendita trovata</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 8 : 12 }}>
            {filtered.map((sale) => {
              const pb = platBadge(sale.piattaforma);
              return (
                <div key={sale.id} className="card" style={{ padding: isMobile ? 12 : 16, borderRadius: 16, transition: 'all 0.15s' }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = sale.profitto_netto >= 0 ? T.green + '40' : T.red + '40'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = T.border}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14 }}>
                    <div style={{ width: isMobile ? 44 : 52, height: isMobile ? 44 : 52, borderRadius: 12, overflow: 'hidden', flexShrink: 0, background: T.elem }}>
                      {sale.product_foto_url ? (
                        <img src={`${API_BASE}${sale.product_foto_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', opacity: 0.3 }}>📦</div>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                        <p style={{ color: T.text, fontWeight: 600, fontSize: isMobile ? '0.82rem' : '0.9rem', fontFamily: T.fontBody, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>
                          {sale.product_name}
                        </p>
                        <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: '0.65rem', fontWeight: 700, background: pb.bg, color: pb.color, border: `1px solid ${pb.border}`, flexShrink: 0 }}>
                          {sale.piattaforma}
                        </span>
                      </div>
                      <p style={{ color: T.muted, fontSize: '0.72rem', fontFamily: T.fontBody, margin: 0 }}>{formatDate(sale.data_vendita)}</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 20, flexShrink: 0 }}>
                      {!isMobile && (
                        <>
                          <div style={{ textAlign: 'right' }}>
                            <p style={{ color: T.muted, fontSize: '0.6rem', textTransform: 'uppercase', margin: 0 }}>Acquisto</p>
                            <p style={{ color: T.text, fontSize: '0.85rem', fontWeight: 500, fontFamily: T.fontBody, margin: 0, opacity: 0.7 }}>{'\u20AC'}{sale.prezzo_acquisto.toFixed(2)}</p>
                          </div>
                          <div style={{ color: T.muted, fontSize: '0.7rem' }}>{'\u2192'}</div>
                          <div style={{ textAlign: 'right' }}>
                            <p style={{ color: T.muted, fontSize: '0.6rem', textTransform: 'uppercase', margin: 0 }}>Vendita</p>
                            <p style={{ color: T.text, fontSize: '0.85rem', fontWeight: 600, fontFamily: T.fontBody, margin: 0 }}>{'\u20AC'}{sale.prezzo_vendita.toFixed(2)}</p>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <p style={{ color: T.muted, fontSize: '0.6rem', textTransform: 'uppercase', margin: 0 }}>Comm.</p>
                            <p style={{ color: T.red, fontSize: '0.85rem', fontWeight: 500, fontFamily: T.fontBody, margin: 0 }}>{'\u20AC'}{sale.commissioni.toFixed(2)}</p>
                          </div>
                        </>
                      )}
                      <div style={{ textAlign: 'center', minWidth: isMobile ? 65 : 75 }}>
                        <p style={{ color: T.muted, fontSize: '0.6rem', textTransform: 'uppercase', margin: 0 }}>Profitto</p>
                        <p style={{ color: sale.profitto_netto >= 0 ? T.green : T.red, fontSize: isMobile ? '0.95rem' : '1.05rem', fontWeight: 800, fontFamily: T.fontBody, margin: 0 }}>
                          {sale.profitto_netto >= 0 ? '+' : ''}{'\u20AC'}{sale.profitto_netto.toFixed(2)}
                        </p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(sale.id); }}
                        disabled={deleting === sale.id}
                        title="Elimina vendita"
                        style={{
                          background: 'rgba(248,113,113,0.1)', border: `1px solid rgba(248,113,113,0.2)`,
                          borderRadius: 8, padding: isMobile ? 6 : 8, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: deleting === sale.id ? 0.5 : 1, transition: 'all 0.15s', flexShrink: 0
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(248,113,113,0.25)'; e.currentTarget.style.borderColor = 'rgba(248,113,113,0.5)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(248,113,113,0.1)'; e.currentTarget.style.borderColor = 'rgba(248,113,113,0.2)'; }}
                      >
                        <svg width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} viewBox="0 0 24 24" fill="none" stroke={T.red} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"/><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  {isMobile && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.border}40` }}>
                      <span style={{ color: T.text, fontSize: '0.72rem', fontFamily: T.fontBody, opacity: 0.7 }}>{'\u20AC'}{sale.prezzo_acquisto.toFixed(2)} {'\u2192'} {'\u20AC'}{sale.prezzo_vendita.toFixed(2)}</span>
                      <span style={{ color: T.red, fontSize: '0.72rem', fontFamily: T.fontBody }}>Comm. {'\u20AC'}{sale.commissioni.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              );
            })}
            <div className="card" style={{ padding: isMobile ? 12 : 16, borderRadius: 16, background: T.elem, borderColor: T.violetDark + '30' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <p style={{ color: T.violet, fontWeight: 700, fontSize: '0.9rem', fontFamily: T.fontBody, margin: 0 }}>TOTALI ({filtered.length})</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 24 }}>
                  {!isMobile && (
                    <>
                      <span style={{ color: T.text, fontWeight: 600, fontSize: '0.9rem', fontFamily: T.fontBody }}>{'\u20AC'}{totals.vendite.toFixed(2)}</span>
                      <span style={{ color: T.red, fontWeight: 600, fontSize: '0.9rem', fontFamily: T.fontBody }}>{'\u20AC'}{totals.commissioni.toFixed(2)}</span>
                    </>
                  )}
                  <span style={{ color: totals.profitto >= 0 ? T.green : T.red, fontWeight: 800, fontSize: isMobile ? '1.1rem' : '1.2rem', fontFamily: T.fontBody }}>
                    {totals.profitto >= 0 ? '+' : ''}{'\u20AC'}{totals.profitto.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </Layout>
  );
}
